import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, MessageSquare, Send, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  getCampaignDashboard,
  updateCampaignSettings,
  sendCampaignTestSms,
} from "@/server/campaign.functions";

export const Route = createFileRoute("/dashboard/admin/campaigns")({
  component: CampaignsPage,
});

function CampaignsPage() {
  const { user, loading, primaryRole } = useAuth();
  const [data, setData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("+16823994461");
  const [testing, setTesting] = useState(false);
  const [template, setTemplate] = useState("");
  const [active, setActive] = useState(false);
  const [smsCap, setSmsCap] = useState(1000);
  const [waCap, setWaCap] = useState(1000);
  const [verCap, setVerCap] = useState(4000);

  const reload = async () => {
    const d: any = await getCampaignDashboard();
    setData(d);
    setActive(d.settings.campaign_active);
    setTemplate(d.settings.sms_body_template);
    setSmsCap(d.settings.daily_sms_cap);
    setWaCap(d.settings.daily_whatsapp_cap);
    setVerCap(d.settings.daily_verifications_cap);
  };

  useEffect(() => {
    if (!loading && primaryRole === "admin") reload();
  }, [loading, primaryRole]);

  const save = async () => {
    setSaving(true);
    try {
      await updateCampaignSettings({
        data: {
          campaign_active: active,
          sms_body_template: template,
          daily_sms_cap: smsCap,
          daily_whatsapp_cap: waCap,
          daily_verifications_cap: verCap,
        },
      });
      toast.success("Settings saved");
      await reload();
    } catch (e: any) {
      toast.error(e.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r: any = await sendCampaignTestSms({ data: { phone: testPhone, first_name: "Dr. Smith" } });
      if (r.ok) toast.success(`Sent to ${r.to} (SID ${r.sid})`);
      else toast.error(`Failed: ${r.error}`);
      await reload();
    } catch (e: any) {
      toast.error(e.message ?? "Send failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !user || primaryRole !== "admin") {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }

  const preview = template
    .replace(/\{first_name\}/g, "Dr. Smith")
    .replace(/\{claim_url\}/g, "https://heallexa.com/claim");

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary/40 via-background to-background">

      <main className="mx-auto max-w-5xl px-6 pt-28 pb-16 space-y-8">
        <Link to="/dashboard/admin" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
        </Link>

        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold tracking-tight">Outreach campaigns</h1>
          <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${active ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>
            {active ? "ACTIVE" : "PAUSED"}
          </div>
        </div>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Metric label="Verifications today" value={`${data.counts.verifications_today} / ${verCap}`} icon={<ShieldCheck className="h-4 w-4" />} />
          <Metric label="SMS sent today" value={`${data.counts.sms_sent_today} / ${smsCap}`} icon={<MessageSquare className="h-4 w-4" />} />
          <Metric label="WhatsApp sent today" value={`${data.counts.whatsapp_sent_today} / ${waCap}`} icon={<Send className="h-4 w-4" />} />
        </section>

        <section className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Campaign master switch</h2>
              <p className="text-sm text-muted-foreground">When OFF, cron ticks return early — no sends happen. Verifications still run.</p>
            </div>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>Daily verifications cap</Label><Input type="number" value={verCap} onChange={(e) => setVerCap(parseInt(e.target.value) || 0)} /></div>
            <div><Label>Daily SMS cap</Label><Input type="number" value={smsCap} onChange={(e) => setSmsCap(parseInt(e.target.value) || 0)} /></div>
            <div><Label>Daily WhatsApp cap</Label><Input type="number" value={waCap} onChange={(e) => setWaCap(parseInt(e.target.value) || 0)} /></div>
          </div>
          <div>
            <Label>SMS body template</Label>
            <Textarea rows={3} value={template} onChange={(e) => setTemplate(e.target.value)} />
            <p className="text-xs text-muted-foreground mt-1">Tokens: <code>{"{first_name}"}</code>, <code>{"{claim_url}"}</code></p>
            <div className="mt-3 p-3 bg-muted/40 rounded-md text-sm"><span className="text-xs uppercase tracking-wider text-muted-foreground">Preview:</span><br />{preview}</div>
          </div>
          <div className="flex justify-end"><Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save settings"}</Button></div>
        </section>

        <section className="rounded-xl border border-border bg-card p-6 space-y-4">
          <h2 className="text-lg font-semibold">Send test SMS</h2>
          <p className="text-sm text-muted-foreground">Sends the current template (with sample first name + claim URL) to a real phone via Twilio.</p>
          <div className="flex gap-2">
            <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+15551234567" />
            <Button onClick={sendTest} disabled={testing}>{testing ? "Sending…" : "Send test"}</Button>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Recent SMS</h2>
          <div className="divide-y divide-border text-sm">
            {(data.sms_log ?? []).slice(0, 20).map((r: any) => (
              <div key={r.id} className="py-2 flex items-center justify-between gap-2">
                <span className="font-mono">{r.recipient_phone}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${r.send_status === "sent" ? "bg-emerald-500/15 text-emerald-700" : "bg-rose-500/15 text-rose-700"}`}>{r.send_status}</span>
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
              </div>
            ))}
            {(!data.sms_log || data.sms_log.length === 0) && <p className="py-2 text-muted-foreground">No SMS yet.</p>}
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Recent WhatsApp</h2>
          <div className="divide-y divide-border text-sm">
            {(data.whatsapp_log ?? []).slice(0, 20).map((r: any) => (
              <div key={r.id} className="py-2 flex items-center justify-between gap-2">
                <span className="font-mono">{r.recipient_phone}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${r.send_status === "sent" ? "bg-emerald-500/15 text-emerald-700" : "bg-rose-500/15 text-rose-700"}`}>{r.send_status}</span>
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
              </div>
            ))}
            {(!data.whatsapp_log || data.whatsapp_log.length === 0) && <p className="py-2 text-muted-foreground">No WhatsApp sends yet.</p>}
          </div>
        </section>
      </main>
    </div>
  );
}

function Metric({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</span>
      </div>
      <p className="text-2xl font-semibold mt-3">{value}</p>
    </div>
  );
}
